<template>
    <section class="p-4 rounded-2xl border border-gray-200 shadow-custom-xl bg-white">
        <form @submit.prevent="submit()" class="space-y-4">
            <Fieldset class="gap-2!">
                <Field>
                    <SearchFormDate v-model="form.startDate" :availableDates />
                    <p class="text-xs text-gray-600 mb-1">{{ $t('Select your arrival date — available days are colored green.') }}</p>
                </Field>
                <Field>
                    <SearchFormNights ref="nightsRef" v-model="form.nights" :paramsSearchFormNights="paramsSearchFormNights" :item="props.item" />
                    <p class="text-xs text-gray-600 mb-1">{{ $t('Select the length of stay. Only options available for the selected arrival date are displayed.') }}</p>
                </Field>
                <Field>
                    <SearchFormGuest v-model:adults="form.adults" v-model:children="form.children" :maxOccupancy="props.item?.maxOccupancy ?? 8" :maxAdults="props.item?.maxAdults ?? 8" :maxChildren="props.item?.maxChildren ?? 8" :maxChildAge="props.item?.maxChildAge ?? 12" />
                    <p class="text-xs text-gray-600 mb-1">{{ $t('Select the number of people and enter the ages of the children.') }}</p>
                </Field>
            </Fieldset>
            <div class="space-y-2">
                <template v-if="verifyAvailabilityResult?.status === 1 && verifiedItem && verifiedAttributes">
                    <div class="flex items-baseline justify-between text-sm text-gray-600">
                        <div>
                            {{ verifiedAttributes.productType?.label }}
                            <br />
                            ({{ $d(verifiedAttributes.checkIn, 'short') }} - {{ $d(verifiedAttributes.checkOut) }})
                        </div>
                        <template v-if="verifiedItem.originalPrice > verifiedItem.displayPrice">
                            <div class="ml-auto text-sm text-primary line-through mr-1">{{ $n(verifiedItem.originalPrice, 'currency') }}</div>
                        </template>
                        <div class="ml-auto font-bold">{{ $n(verifiedItem.displayPrice, 'currency') }}</div>
                    </div>
                    <!-- breakdown from verify-availability API -->
                    <template v-for="(cost, index) in breakdown" :key="index">
                        <div class="flex gap-4 text-gray-600 text-sm">
                            <div class="flex-1">{{ cost.label ?? cost.name ?? cost.label_key }}</div>
                            <div>
                                {{ $n(Number(cost.total ?? cost.price ?? 0), 'currency') }}
                                <template v-if="cost.payAtLocation">*</template>
                            </div>
                        </div>
                    </template>
                    <div class="pt-3 border-t border-gray-300">
                        <div class="flex justify-between gap-4 text-lg font-semibold space-y-1 text-gray-800">
                            <div>{{ $t('Total') }}</div>
                            <div>{{ $n(totalPrice, 'currency') }}</div>
                        </div>
                        <div class="text-xs text-gray-600">{{ $t('Items marked with * are paid at the property.') }}</div>
                    </div>
                    <Message severity="primary">{{ $t('We must check the availability of accommodation in the selected period.') }}</Message>
                    <Button type="submit" severity="primary" size="lg" class="w-full" :loading="form.processing" :disabled="isOverCapacity || form.processing">
                        {{ $t('Send inquiry') }}
                    </Button>
                </template>

                <template v-else>
                    <Message severity="primary" class="text-center"><span v-html="$t('Please select availability dates to see the price and book.')"></span></Message>
                </template>
            </div>
        </form>
    </section>
</template>

<script setup>
import { useBookingStore } from '@/stores/booking'
import { useToast } from '@/composables/toast/useToast.js'
import { useCraftNavigation } from '@/composables/useCraftNavigation'
import { storeToRefs } from 'pinia'
import { useProductPriceAvailabilityStore } from '@/stores/productPriceAvailability'

const booking = useBookingStore()
const availabilityStore = useProductPriceAvailabilityStore()
const toast = useToast()
const { reloadTo } = useCraftNavigation()

const { totalPrice, form, maxOccupancy, isOverCapacity, verifyAvailabilityResult } = storeToRefs(booking)
const { availableDates } = storeToRefs(availabilityStore)

const nightsRef = ref(null)

const props = defineProps({
    item: Object
})

const breakdown = computed(() => {
    const list = verifyAvailabilityResult.value?.item?.attributes?.breakdown
    return Array.isArray(list) ? list : []
})

const verifiedItem = computed(() => verifyAvailabilityResult.value?.item ?? null)
const verifiedAttributes = computed(() => verifiedItem.value?.attributes ?? null)

const paramsSearchFormNights = computed(() => availabilityStore.getAvailableNights(form.value.startDate))

watch(
    () => form.value.startDate,
    async (startDate, previousStartDate) => {
        if (!startDate || !form.value.productId) {
            return
        }

        await availabilityStore.loadByStartDate(startDate)

        const nights = availabilityStore.getAvailableNights(startDate)

        if (nights.length > 0 && !nights.includes(Number(form.value.nights))) {
            form.value.nights = nights[0]
        }

        if (previousStartDate !== undefined) {
            nightsRef.value?.scrollIntoView()
            nightsRef.value?.open()
        }
    },
    { immediate: true, flush: 'post' }
)
/** Submit booking → cart */
const submit = () => {
    if (isOverCapacity.value) {
        toast.error(`Max occupancy is ${maxOccupancy.value}`)
        return
    }
    return booking.submitBooking().then(() => {
        reloadTo('/cart')
    })
}
</script>
